// src/finance/components/FlowBar.tsx
// Barre de répartition du CA : on doit voir la part qui reste avant de lire un
// chiffre. Présentation pure — les parts viennent de revenueSplit (payers.ts).
import type { CSSProperties } from 'react'
import type { SplitKey, SplitSegment } from '../payers'
import { fmtEur } from '../format'

const COLOR: Record<SplitKey, string> = {
  expenses: 'var(--bronze)',
  company: 'var(--charge-deep)',
  personal: 'var(--charge)',
  living: 'var(--living)',
  net: 'var(--net)',
}

/** En dessous, le segment est trop étroit pour porter un texte lisible. */
const MIN_SHARE_FOR_LABEL = 0.08
/** Au-delà, le segment net a la place d'expliciter ce qu'il représente. */
const MIN_SHARE_FOR_CAPTION = 0.3

const pct = (share: number) => `${Math.round(share * 100)} %`

const segmentStyle = (segment: SplitSegment): CSSProperties => ({
  flex: segment.share,
  minWidth: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  background: COLOR[segment.key],
  color: '#15140f',
  fontSize: '0.625rem',
  fontWeight: 700,
  letterSpacing: '0.02em',
})

function segmentText(segment: SplitSegment): string {
  if (segment.share < MIN_SHARE_FOR_LABEL) return ''
  if (segment.key === 'net' && segment.share >= MIN_SHARE_FOR_CAPTION) {
    return `${pct(segment.share)} — ce qui te reste`
  }
  return pct(segment.share)
}

export function FlowBar({
  segments,
  deficit,
}: {
  segments: SplitSegment[]
  /**
   * Montant dont les dépenses société dépassent le CA. Fourni (> 0), il REMPLACE
   * la barre : les parts sont alors rapportées à un CA plus petit que ce qu'elles
   * mesurent, et le segment des dépenses s'affichait à « 150 % ».
   */
  deficit?: number | undefined
}) {
  if (deficit !== undefined && deficit > 0) {
    return (
      <div
        data-testid="flowbar-loss"
        style={{
          border: '1px solid var(--arancio)',
          borderRadius: '6px',
          padding: '0.5rem 0.625rem',
          fontSize: '0.75rem',
          color: 'var(--arancio)',
          display: 'flex',
          alignItems: 'baseline',
          gap: '0.5rem',
          flexWrap: 'wrap',
        }}
      >
        <b style={{ fontFamily: 'var(--mono)', fontVariantNumeric: 'tabular-nums', fontSize: '0.9375rem' }}>
          {fmtEur(-deficit)}
        </b>
        <span style={{ color: 'var(--muted)' }}>
          de déficit /mois — rien à répartir tant que le CA ne couvre pas les dépenses société.
        </span>
      </div>
    )
  }

  const visible = segments.filter((s) => s.share > 0)
  if (visible.length === 0) return null

  const description = visible.map((s) => `${s.label} ${pct(s.share)}`).join(', ')

  return (
    <div data-testid="flowbar" style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
      <div
        role="img"
        aria-label={`Répartition du CA : ${description}`}
        style={{
          display: 'flex',
          height: '34px',
          borderRadius: '6px',
          overflow: 'hidden',
          gap: '2px',
          background: 'var(--bg)',
        }}
      >
        {visible.map((segment) => (
          <div key={segment.key} style={segmentStyle(segment)}>
            {segmentText(segment)}
          </div>
        ))}
      </div>
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '0.25rem 1rem',
          fontSize: '0.6875rem',
          color: 'var(--muted)',
        }}
      >
        {visible.map((segment) => (
          <span key={segment.key} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}>
            <i
              style={{
                width: '8px',
                height: '8px',
                borderRadius: '2px',
                background: COLOR[segment.key],
                flexShrink: 0,
              }}
            />
            {segment.label}
            <b
              style={{
                color: 'var(--fg)',
                fontFamily: 'var(--mono)',
                fontVariantNumeric: 'tabular-nums',
                fontWeight: 600,
              }}
            >
              {fmtEur(segment.amount)}
            </b>
          </span>
        ))}
      </div>
    </div>
  )
}
